import { useRef, useState, useEffect } from 'react'
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion'
import { Link } from 'react-router-dom'
import { Camera, ArrowRight, Sparkles, Filter, X, ChevronLeft, ChevronRight } from 'lucide-react'
import { getImagesForFilter, type GalleryFilterId } from '../data/galleryImages'

// Optimierte Gallery Image Komponente mit echtem Lazy Loading
function GalleryImage({ 
  image, 
  index, 
  onClick 
}: { 
  image: { id: string; src: string; category: string; title: string }
  index: number
  onClick: () => void
}) {
  const [isLoaded, setIsLoaded] = useState(false)
  const [isInView, setIsInView] = useState(false)
  const imgRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true)
            observer.unobserve(entry.target)
          }
        })
      },
      { rootMargin: '50px' } // Starte Ladevorgang 50px bevor Bild sichtbar wird
    )

    if (imgRef.current) {
      observer.observe(imgRef.current)
    }

    return () => {
      if (imgRef.current) {
        observer.unobserve(imgRef.current)
      }
    }
  }, [])

  return (
    <motion.div
      ref={imgRef}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(index * 0.02, 0.5) }}
      className="group cursor-pointer"
      onClick={onClick}
    >
      <div className="relative overflow-hidden rounded-xl border border-white/5 group-hover:border-brand-800/30 transition-all duration-500">
        <div className="aspect-[4/3] overflow-hidden bg-white/5 relative">
          {/* Placeholder während Bild lädt */}
          {!isLoaded && (
            <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-white/10 animate-pulse" />
          )}
          {/* Bild nur laden wenn im Viewport */}
          {isInView && (
            <img
              src={image.src}
              alt={image.category}
              onLoad={() => setIsLoaded(true)}
              className={`w-full h-full object-cover transition-all duration-700 group-hover:scale-105 ${
                isLoaded ? 'opacity-100' : 'opacity-0'
              }`}
              decoding="async"
            />
          )}
        </div>
        
        {/* Hover Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
        
        {/* Category Badge on Hover */}
        <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-full group-hover:translate-y-0 transition-transform duration-500">
          <span className="px-3 py-1.5 bg-brand-800/90 text-white text-xs uppercase tracking-wider rounded-full">
            {image.category}
          </span>
        </div>
      </div>
    </motion.div>
  )
}

// Filter-Kategorien: Fest-Scheune/Hochzeiten zuerst, Firmenfeiern bewusst weiter unten (weniger partylastig)
const filterCategories: { id: GalleryFilterId; label: string; folder: string }[] = [
  { id: 'all', label: 'Alle', folder: '' },
  { id: 'hochzeit', label: 'Hochzeiten', folder: 'hochzeit_events' },
  { id: 'fest_scheune', label: 'Fest-Scheune', folder: 'raum/fest_scheune' },
  { id: 'innenhof', label: 'Innenhof', folder: 'raum/innenhof' },
  { id: 'landhaus', label: 'Landhaus', folder: 'raum/landhaus' },
  { id: 'weinstube', label: 'Weinstube', folder: 'raum/weinstube' },
  { id: 'garten', label: 'Garten', folder: 'raum/garten' },
  { id: 'firmen', label: 'Firmenfeiern', folder: 'firmen_events' },
]

// Statische Hero-Bilder (unabhängig von der Datenbank)
const heroImages = [
  { id: 'hero-geschichte', src: '/assets/heros/geschichte.jpg', category: 'Geschichte', title: 'Geschichte' },
  { id: 'hero-hochzeit', src: '/assets/heros/hochzeit.jpg', category: 'Hochzeiten', title: 'Hochzeit' },
  { id: 'hero-firmenevent', src: '/assets/heros/firmenevent.jpg', category: 'Firmenfeiern', title: 'Firmenevent' },
  { id: 'hero-veranstaltung', src: '/assets/heros/ihre_veranstaltung.jpg', category: 'Veranstaltungen', title: 'Ihre Veranstaltung' },
  { id: 'hero-brunch', src: '/assets/heros/brunch.jpg', category: 'Brunch', title: 'Brunch' },
  { id: 'hero-partys', src: '/assets/heros/partys.jpg', category: 'Partys', title: 'Partys' },
  { id: 'hero-kontakt', src: '/assets/heros/kontakt.jpg', category: 'Kontakt', title: 'Kontakt' },
  { id: 'hero-innenhof', src: '/assets/heros/innenhof.jpg', category: 'Innenhof', title: 'Innenhof' },
  { id: 'hero-festscheune', src: '/assets/heros/festscheune.jpg', category: 'Fest-Scheune', title: 'Fest-Scheune' },
  { id: 'hero-landhaus', src: '/assets/heros/landhaus.jpg', category: 'Landhaus', title: 'Landhaus' },
]

export default function Galerie() {
  const heroRef = useRef<HTMLDivElement>(null)
  const { scrollYProgress } = useScroll()
  const heroOpacity = useTransform(scrollYProgress, [0, 0.15], [1, 0])
  const heroScale = useTransform(scrollYProgress, [0, 0.15], [1, 1.1])

  const [activeFilter, setActiveFilter] = useState<GalleryFilterId>('all')
  const [galleryImages, setGalleryImages] = useState<Array<{ id: string; src: string; category: string; title: string }>>([])
  const [isLoading, setIsLoading] = useState(true)
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [lightboxIndex, setLightboxIndex] = useState(0)
  const [totalCount, setTotalCount] = useState(0)
  const isLoadingMore = false

  // Lade Bilder statisch aus galerie_neu
  useEffect(() => {
    setIsLoading(true)
    const staticImages = getImagesForFilter(activeFilter)
    setGalleryImages(staticImages)
    setTotalCount(staticImages.length)
    setIsLoading(false)
  }, [activeFilter])

  // Keyboard Navigation für Lightbox
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!lightboxOpen) return
      
      if (e.key === 'Escape') {
        setLightboxOpen(false)
      } else if (e.key === 'ArrowLeft') {
        setLightboxIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length)
      } else if (e.key === 'ArrowRight') {
        setLightboxIndex((prev) => (prev + 1) % galleryImages.length)
      }
    }
    
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [lightboxOpen, galleryImages.length])

  return (
    <div className="bg-[#0a0a0a]">
      
      {/* ════════════════════════════════════════════════════════════
          HERO SECTION - Compact Banner
      ════════════════════════════════════════════════════════════ */}
      <section ref={heroRef} className="relative h-[50vh] min-h-[400px] max-h-[550px] w-full overflow-hidden">
        {/* Image Background */}
        <motion.div style={{ scale: heroScale }} className="absolute inset-0">
          <img
            src="/assets/heros/hero_bilder.jpg"
            alt="Galerie Hückels May"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ filter: 'brightness(0.3)' }}
          />
        </motion.div>

        {/* Gradient Overlays */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-transparent to-[#0a0a0a]" />
        
        {/* Hero Content */}
        <motion.div 
          style={{ opacity: heroOpacity }}
          className="relative z-10 h-full flex flex-col justify-center items-center text-center px-6 pt-16"
        >
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mb-4"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 backdrop-blur-xl border border-white/10 rounded-full">
              <Camera className="w-3 h-3 text-brand-400" />
              <span className="text-white/70 text-xs uppercase tracking-[0.25em]">Impressionen</span>
            </div>
          </motion.div>

          {/* Main Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="font-serif text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-white font-light tracking-tight leading-[1.1] mb-4"
          >
            Unsere <span className="italic text-white/70">Galerie</span>
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.5 }}
            className="text-white/50 text-xs sm:text-sm md:text-base max-w-xl leading-relaxed px-4"
          >
            Eindrücke unserer Räume und der einzigartigen Atmosphäre – von festlich bis entspannt.
          </motion.p>
        </motion.div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          FILTER SECTION
      ════════════════════════════════════════════════════════════ */}
      <section className="py-8 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex flex-col items-center gap-6"
          >
            {/* Filter-Titel */}
            <div className="flex items-center gap-3">
              <Filter className="w-4 h-4 text-brand-400" />
              <span className="text-white/60 text-sm uppercase tracking-[0.2em]">Filter</span>
            </div>

            {/* Filter-Buttons */}
            <div className="flex flex-wrap justify-center gap-2 px-2">
              {filterCategories.map((category) => (
                <motion.button
                  key={category.id}
                  onClick={() => setActiveFilter(category.id)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className={`px-3 sm:px-4 md:px-5 py-2 md:py-2.5 rounded-full text-xs uppercase tracking-[0.15em] font-semibold transition-all duration-300 ${
                    activeFilter === category.id
                      ? 'bg-white text-black'
                      : 'bg-white/5 text-white/60 border border-white/10 hover:border-white/30 hover:text-white'
                  }`}
                >
                  {category.label}
                </motion.button>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          GALLERY GRID
      ════════════════════════════════════════════════════════════ */}
      <section className="relative py-16 md:py-24">
        {/* Background Glow */}
        <div className="absolute top-40 right-20 w-[500px] h-[500px] bg-brand-800/5 rounded-full blur-[120px]" />

        <div className="max-w-[1400px] mx-auto px-6 relative z-10">
          {isLoading ? (
            <div className="text-center py-20">
              <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-brand-400" />
              <p className="mt-4 text-white/40">Bilder werden geladen...</p>
            </div>
          ) : galleryImages.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-20"
            >
              <Camera className="w-16 h-16 text-white/20 mx-auto mb-4" />
              <p className="text-xl text-white/40">Noch keine Bilder in dieser Kategorie.</p>
            </motion.div>
          ) : (
            <motion.div
              key={activeFilter}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-2 sm:gap-3 md:gap-4"
            >
              {galleryImages.map((image, index) => (
                <GalleryImage
                  key={image.id}
                  image={image}
                  index={index}
                  onClick={() => {
                    setLightboxIndex(index)
                    setLightboxOpen(true)
                  }}
                />
              ))}
              
              {/* Infinite Scroll Sentinel */}
              {galleryImages.length < totalCount && (
                <div id="gallery-sentinel" className="w-full h-20 flex items-center justify-center">
                  {isLoadingMore && (
                    <div className="text-center">
                      <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-brand-400" />
                      <p className="mt-2 text-white/40 text-sm">Weitere Bilder werden geladen...</p>
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          )}
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          CTA SECTION
      ════════════════════════════════════════════════════════════ */}
      <section className="py-12 md:py-20 border-t border-white/5">
        <div className="max-w-3xl mx-auto px-6 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-brand-800/10 border border-brand-800/20 mb-8">
              <Sparkles className="w-7 h-7 text-brand-400" />
            </div>
            
            <p className="text-brand-400 text-xs uppercase tracking-[0.4em] mb-6">Begeistert?</p>
            
            <h2 className="font-serif text-3xl md:text-5xl text-white font-light mb-8 leading-tight">
              Erleben Sie die Atmosphäre<br />
              <span className="italic text-white/60">selbst</span>
            </h2>
            
            <p className="text-white/40 text-lg mb-10 max-w-2xl mx-auto">
              Lassen Sie sich von unseren Räumlichkeiten inspirieren und planen Sie Ihr eigenes Event.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/kontakt">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                  className="px-6 md:px-8 lg:px-10 py-3 md:py-4 bg-white text-black text-xs md:text-sm uppercase tracking-[0.2em] font-bold rounded-full hover:bg-brand-800 hover:text-white transition-all duration-500 flex items-center gap-2 md:gap-3"
                >
                  Besichtigung anfragen
                  <Sparkles className="w-4 h-4" />
                </motion.button>
              </Link>
              <Link to="/raeume">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                  className="px-6 md:px-8 lg:px-10 py-3 md:py-4 border border-white/30 text-white text-xs md:text-sm uppercase tracking-[0.2em] font-bold rounded-full hover:bg-white/10 transition-all duration-500 flex items-center gap-2 md:gap-3"
                >
                  Unsere Räume
                  <ArrowRight className="w-4 h-4" />
                </motion.button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          LIGHTBOX
      ════════════════════════════════════════════════════════════ */}
      <AnimatePresence>
        {lightboxOpen && galleryImages.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] bg-black/95 flex items-center justify-center"
            onClick={() => setLightboxOpen(false)}
          >
            {/* Close Button */}
            <button
              onClick={() => setLightboxOpen(false)}
              className="absolute top-6 right-6 z-[10000] p-3 bg-white/10 hover:bg-white/20 rounded-full text-white transition-all"
            >
              <X className="h-6 w-6" />
            </button>

            {/* Navigation */}
            {galleryImages.length > 1 && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    setLightboxIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length)
                  }}
                  className="absolute left-6 z-[10000] p-3 bg-white/10 hover:bg-white/20 rounded-full text-white transition-all"
                >
                  <ChevronLeft className="h-8 w-8" />
                </button>
                
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    setLightboxIndex((prev) => (prev + 1) % galleryImages.length)
                  }}
                  className="absolute right-6 z-[10000] p-3 bg-white/10 hover:bg-white/20 rounded-full text-white transition-all"
                >
                  <ChevronRight className="h-8 w-8" />
                </button>
              </>
            )}

            {/* Counter */}
            <div className="absolute top-6 left-1/2 -translate-x-1/2 z-[10000] px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white text-sm">
              {lightboxIndex + 1} / {galleryImages.length}
            </div>

            {/* Image */}
            <motion.div
              key={lightboxIndex}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.3 }}
              className="max-w-7xl max-h-[90vh] mx-auto px-4"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={galleryImages[lightboxIndex].src}
                alt={galleryImages[lightboxIndex].category}
                className="max-w-full max-h-[85vh] object-contain rounded-lg"
              />
              
              <div className="mt-4 text-center">
                <span className="px-4 py-2 bg-brand-800/90 text-white text-sm uppercase tracking-wider rounded-full">
                  {galleryImages[lightboxIndex].category}
                </span>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  )
}
